import { useState } from 'react';
import { X, Plus, Send, Split, Wallet, Printer, Construction } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { STATE_META, money, people, phones } from '@/components/tables/tableStates';
import { fmtElapsed } from '../shared';

const ITEM_STATUS = { 'Nuevo': ['Nuevo', 'm-accent-text'], 'Confirmado': ['Confirmado', 'm-accent-text'], 'En preparación': ['En preparación', 'text-[#92400e]'],
  'Listo': ['Listo', 'text-[#166534]'], 'Entregado': ['Servido', 'text-[#166534]'] };

const Tabs = ({ tabs, tab, setTab }) => (
  <div className="flex gap-5 border-b border-border" role="tablist">
    {tabs.map(([k, l]) => (
      <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`unit-tab-${k}`}
        className="m-tab pb-2 text-[15px] font-medium text-muted-foreground">{l}</button>
    ))}
  </div>
);

const OrderLines = ({ orders }) => {
  const lines = orders.flatMap(o => (o.items || []).map((it, i) => ({ ...it, key: `${o.order_id}-${i}`, orderStatus: o.status })));
  if (!lines.length) return <p className="text-sm text-muted-foreground py-6 text-center">Sin pedidos todavía</p>;
  return (
    <ul className="divide-y divide-border" data-testid="unit-order-lines">
      {lines.map(l => {
        const [label, cls] = ITEM_STATUS[l.status && l.status !== 'Pendiente' ? l.status : l.orderStatus] || [l.orderStatus, 'text-muted-foreground'];
        return (
          <li key={l.key} className="flex items-center gap-3 py-2.5">
            <span className="w-7 h-7 rounded-md bg-muted grid place-items-center text-sm font-semibold">{l.quantity}</span>
            <span className="flex-1 min-w-0">
              <span className="block font-medium truncate">{l.product_name}</span>
              <span className={`block text-xs font-semibold ${cls}`}>{label}</span>
            </span>
            <span className="font-semibold tabular-nums">{money(l.subtotal)}</span>
          </li>
        );
      })}
    </ul>
  );
};

const Prototype = () => (
  <div className="space-y-3" data-testid="rental-prototype">
    <div className="flex items-center gap-2 rounded-lg bg-[#fef3c7] text-[#78350f] px-3 py-2 text-sm font-semibold"><Construction className="w-4 h-4" />Prototipo · datos de ejemplo, sin guardar</div>
    {[['Plan', 'Temporada completa'], ['Desde', '01/12/2025'], ['Hasta', '28/02/2026'], ['Titular', 'Familia Pérez'], ['Saldo', '$ 180.000']].map(([k, v]) => (
      <div key={k} className="flex justify-between text-sm"><span className="text-muted-foreground">{k}</span><span className="font-semibold">{v}</span></div>
    ))}
  </div>
);

export const UnitPanel = ({ unit, details, now, isSuper, canCharge, onClose, onAddItem, onSendKitchen, onSplit, onCharge, onPrint, onOpen, opening }) => {
  const [tab, setTab] = useState('order');
  const meta = STATE_META[unit.state] || STATE_META.free;
  const beach = ['umbrella', 'tent'].includes(unit.unit_type);
  const tabs = beach ? [['order', 'Consumo'], ...(isSuper ? [['rental', 'Alquiler']] : []), ['client', 'Cliente'], ['history', 'Historial']]
    : [['order', 'Pedido'], ['client', 'Cliente'], ['notes', 'Notas'], ['history', 'Historial']];
  const orders = details?.orders || [];
  const notes = orders.flatMap(o => [o.notes, ...(o.items || []).map(i => i.notes && `${i.product_name}: ${i.notes}`)]).filter(Boolean);
  const hasNew = orders.some(o => o.status === 'Nuevo');
  return (
    <aside className="m-card w-full lg:w-[380px] shrink-0 flex flex-col p-5 gap-4 lg:max-h-[calc(100vh-120px)] lg:sticky lg:top-[72px]" data-testid="unit-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold" data-testid="unit-panel-name">{unit.name}</h2>
          <p className="text-sm font-semibold flex items-center gap-1.5 mt-0.5" data-testid="unit-panel-state">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
            {meta.label}{unit.session_id ? ` · ${fmtElapsed(unit.opened_at, now)}` : ''}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {unit.session_id ? [people(unit.guest_count), unit.waiter_name, phones(unit.devices || 0) + ' conectados'].filter(Boolean).join(' · ') : `Capacidad ${unit.capacity}`}
          </p>
        </div>
        <Button variant="outline" size="icon" onClick={onClose} aria-label="Cerrar panel" data-testid="unit-panel-close"><X className="w-4 h-4" /></Button>
      </div>
      {!unit.session_id ? (
        <div className="flex-1 flex flex-col justify-center gap-3 py-8 text-center">
          <p className="text-muted-foreground">{unit.state === 'reserved' && unit.reservation ? `Reservada ${unit.reservation.time} · ${unit.reservation.customer_name}` : 'Mesa sin cuenta abierta'}</p>
          <Button className="h-12 bg-[#2563EB] hover:bg-[#1d4ed8] text-white" onClick={onOpen} disabled={opening} data-testid="unit-open-btn">Abrir mesa</Button>
        </div>
      ) : (
        <>
          <Tabs tabs={tabs} tab={tab} setTab={setTab} />
          <div className="flex-1 overflow-y-auto min-h-[160px]">
            {tab === 'order' && <OrderLines orders={orders} />}
            {tab === 'rental' && <Prototype />}
            {tab === 'client' && (
              <ul className="space-y-2 text-sm" data-testid="unit-clients">
                {(details?.participants || []).map(p => (
                  <li key={p.participant_id} className="flex justify-between rounded-lg bg-muted px-3 py-2"><span>{p.name}</span><span className="font-semibold">{money(p.subtotal)}</span></li>
                ))}
                {!details?.participants?.length && <li className="text-muted-foreground">Nadie se sumó desde el QR todavía</li>}
              </ul>
            )}
            {tab === 'notes' && (
              <ul className="space-y-2 text-sm" data-testid="unit-notes">
                {notes.map((n, i) => <li key={i} className="rounded-lg bg-[#fef3c7] text-[#78350f] px-3 py-2">{n}</li>)}
                {!notes.length && <li className="text-muted-foreground">Sin notas</li>}
              </ul>
            )}
            {tab === 'history' && (
              <ul className="space-y-2 text-sm" data-testid="unit-history">
                {orders.map(o => (
                  <li key={o.order_id} className="flex justify-between border-b border-border pb-2">
                    <span>#{o.order_id.slice(-4)} · {new Date(o.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</span>
                    <span className="font-medium">{o.status} · {money(o.total)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex items-end justify-between pt-2">
            <span className="font-semibold">Total parcial</span>
            <span className="text-3xl font-bold tabular-nums" data-testid="unit-panel-total">{money(details?.total ?? unit.total)}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-12" onClick={onAddItem} data-testid="unit-add-item-btn"><Plus className="w-4 h-4 mr-1" />Agregar ítem</Button>
            <Button className="h-12 bg-[#2563EB] hover:bg-[#1d4ed8] text-white" disabled={!hasNew} onClick={onSendKitchen} data-testid="unit-send-kitchen-btn"><Send className="w-4 h-4 mr-1" />Enviar a cocina</Button>
            <Button variant="outline" className="h-12" disabled={!canCharge} onClick={onSplit} data-testid="unit-split-btn"><Split className="w-4 h-4 mr-1" />Dividir cuenta</Button>
            <Button className="h-12 bg-[#111827] hover:bg-black text-white dark:bg-white dark:text-[#111827]" disabled={!canCharge} onClick={onCharge} data-testid="unit-charge-btn"><Wallet className="w-4 h-4 mr-1" />Cobrar</Button>
          </div>
          <Button variant="ghost" size="sm" onClick={onPrint} data-testid="unit-print-btn"><Printer className="w-4 h-4 mr-1" />Imprimir pre-cuenta</Button>
        </>
      )}
    </aside>
  );
};
